import type { StateCreator } from 'zustand';
import type { ChatSessionDto } from '@/lib/api/sessions.client';
import type { IChat, IMessage } from '@/lib/types/chat';
import { bumpRow, listedAsOlder, placeRow, rewriteListsHolding } from '@/lib/stores/chat-list-rows';
import { loadChat, type LoadChatOptions } from '@/lib/stores/chat-store/load-chat';
import { openChatRefreshed } from '@/lib/stores/chat-store/session-dto';
import { readSessionRow } from '@/lib/stores/chat-store/tenant-access';
import type { ChatState, GetChatState, SetChatState } from '@/lib/stores/chat.store';
import { sessionToHistory } from '@/lib/stores/session-history';

/** The chat on screen of the chat store (KAN-1266): opening it and keeping it current. */
interface OpenChatData {
  currentChat: IChat | null;
  activeChatId: string | null;
  // True when the last loadChat could not resolve the session (deleted, or
  // no access — e.g. someone opened a foreign /chat/{id} link without a
  // share grant). Lets the UI show a "not found" state instead of hanging
  // on the loading title forever.
  chatLoadError: boolean;
}

export interface OpenChatSlice extends OpenChatData {
  setCurrentChat: (chat: IChat | null) => void;
  setActiveChatId: (chatId: string | null) => void;
  /**
   * ``signal`` aborts the workspace switch a load may need: a caller that has
   * moved on (route change, effect cleanup) must not drag the tab into the
   * workspace of a chat nobody is looking at any more.
   */
  loadChat: (chatId: string, options?: LoadChatOptions) => Promise<void>;
  /**
   * Forget a chat's project assignment locally, after the server has been
   * told. Purely local because the caller owns the request.
   *
   * Load-bearing for the redirect, not cosmetic: ``ProjectPanelHost`` sends
   * ``/chat/{id}`` back into the project frame for as long as
   * ``currentChat.projectId`` claims the chat lives there. A chat taken out of
   * its project therefore has to lose the flag here too, or the bounce-back
   * undoes the navigation away from the project one frame later.
   */
  clearChatProject: (chatId: string) => void;
  /**
   * A chat's stored messages, as a read of them found them (KAN-1754). The
   * open chat takes them; a list holding the chat moves it up only when they
   * hold activity the list does not know yet.
   */
  applyStoredMessages: (sessionId: string, messages: IMessage[]) => void;
  refreshSessionMetadata: (chatId: string) => Promise<void>;
  /** A session row read elsewhere (title, agent state), into the lists and the open chat. */
  applySessionRow: (session: ChatSessionDto) => void;
}

/** No chat open, at start and after a reset. */
export const initialOpenChatState = (): OpenChatData => ({
  currentChat: null,
  activeChatId: null,
  chatLoadError: false,
});

const refreshSessionMetadata = async (set: SetChatState, get: GetChatState, chatId: string) => {
  if (!chatId) return;
  const session = await readSessionRow(chatId, get().sessionsLimit);
  if (session) applySessionRow(set, session);
};

const applySessionRow = (set: SetChatState, session: ChatSessionDto) => {
  const entry = sessionToHistory(session);
  set((state) => ({
    ...rewriteListsHolding(state, session.id, (list) => placeRow(list, entry, false)),
    currentChat:
      state.currentChat?.id === session.id
        ? openChatRefreshed(state.currentChat, session)
        : state.currentChat,
  }));
};

const clearChatProject = (set: SetChatState, chatId: string) => {
  set((state) =>
    state.currentChat?.id === chatId
      ? { currentChat: { ...state.currentChat, projectId: null, projectShared: false } }
      : {}
  );
};

// One write for every read of a chat's stored messages, whoever read them
// (KAN-1754). The open chat takes them as they are. Every own list holding the
// chat moves it to the top, with the time of its newest message, when that
// message is newer than the activity the list shows: an answer or a question
// that landed since, here or elsewhere. Opening a chat, or reading it again
// after a stop that stored nothing, moves nothing. Before, the places chose:
// some always moved the chat, with the time of the moment, others never did.
// A chat no list holds is not added: which list a chat belongs in is the
// server's to say, and rows made up here put background agent and project
// chats into the plain sidebar.
const applyStoredMessages = (set: SetChatState, sessionId: string, messages: IMessage[]) => {
  const newest = newestMessageAt(messages);
  set((state) => ({
    ...(newest !== null && listedAsOlder(state, sessionId, newest)
      ? rewriteListsHolding(state, sessionId, bumpRow(sessionId, new Date(newest).toISOString()))
      : {}),
    currentChat:
      state.currentChat?.id === sessionId
        ? { ...state.currentChat, messages, updatedAt: new Date() }
        : state.currentChat,
  }));
};

/** When the newest of the messages was written; `null` for none. */
const newestMessageAt = (messages: IMessage[]): number | null => {
  const times = messages
    .map((message) => new Date(message.timestamp).getTime())
    .filter(Number.isFinite);
  return times.length > 0 ? Math.max(...times) : null;
};

export const createOpenChatSlice: StateCreator<ChatState, [], [], OpenChatSlice> = (set, get) => ({
  ...initialOpenChatState(),
  setCurrentChat: (chat) => set({ currentChat: chat }),
  setActiveChatId: (chatId) => set({ activeChatId: chatId }),
  loadChat: (chatId, options) => loadChat(set, get, chatId, options),
  clearChatProject: (chatId) => clearChatProject(set, chatId),
  applyStoredMessages: (sessionId, messages) => applyStoredMessages(set, sessionId, messages),
  refreshSessionMetadata: (chatId) => refreshSessionMetadata(set, get, chatId),
  applySessionRow: (session) => applySessionRow(set, session),
});
